import { useState } from "react";
import { MarketRecapTemplate } from "../../types/market-recap";
import { cn } from "../../lib/utils";

interface TemplateStepProps {
  selectedTemplateId: string | null;
  onSelect: (templateId: string, templateName: string, previewUrl?: string) => void;
}

// Hardcoded market recap templates based on theme.json
const MARKET_RECAP_TEMPLATES: MarketRecapTemplate[] = [
  {
    id: "market-recap/layout_AAPL",
    name: "Minimalist Light",
    theme: "light",
    description: "Clean, Apple-inspired minimal aesthetic with white background",
    previewUrl: "/api/templates/market-recap/layout_AAPL/preview.png",
  },
  {
    id: "market-recap/layout_MSFT",
    name: "Corporate Dashboard",
    theme: "dark",
    description: "Enterprise BI style with dark navy background and blue accents",
    previewUrl: "/api/templates/market-recap/layout_MSFT/preview.png",
  },
  {
    id: "market-recap/layout_GOOGL",
    name: "Split Hero",
    theme: "split",
    description: "Bold asymmetric split layout with two-tone background",
    previewUrl: "/api/templates/market-recap/layout_GOOGL/preview.png",
  },
  {
    id: "market-recap/layout_NVDA",
    name: "Data-Dense Dark",
    theme: "terminal",
    description: "Trading terminal / Bloomberg-style with green-on-black",
    previewUrl: "/api/templates/market-recap/layout_NVDA/preview.png",
  },
  {
    id: "market-recap/layout_TSLA",
    name: "Bold Gradient",
    theme: "gradient",
    description: "Electric, futuristic with purple gradient and glowing effects",
    previewUrl: "/api/templates/market-recap/layout_TSLA/preview.png",
  },
];

// Theme color mapping for placeholder backgrounds
const THEME_COLORS: Record<string, { bg: string; accent: string }> = {
  light: { bg: "bg-gray-100", accent: "from-gray-200 to-gray-300" },
  dark: { bg: "bg-slate-800", accent: "from-blue-600 to-blue-800" },
  split: { bg: "bg-gradient-to-r from-white to-slate-800", accent: "from-red-500 to-blue-500" },
  terminal: { bg: "bg-black", accent: "from-green-500 to-green-700" },
  gradient: { bg: "bg-gradient-to-br from-purple-600 to-pink-600", accent: "from-purple-400 to-pink-400" },
};

export function TemplateStep({ selectedTemplateId, onSelect }: TemplateStepProps) {
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const handleImageError = (templateId: string) => {
    setImageErrors((prev) => ({ ...prev, [templateId]: true }));
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-foreground">
          Choose your visual style
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Select a template that matches your presentation needs
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {MARKET_RECAP_TEMPLATES.map((template) => {
          const isSelected = selectedTemplateId === template.id;
          const hasImageError = imageErrors[template.id];
          const colors = THEME_COLORS[template.theme] || THEME_COLORS.light;

          return (
            <button
              key={template.id}
              onClick={() => onSelect(template.id, template.name, template.previewUrl)}
              className={cn(
                "group relative flex flex-col overflow-hidden rounded-xl border-2 transition-all text-left",
                "hover:border-primary/50 hover:shadow-lg",
                isSelected
                  ? "border-primary ring-2 ring-primary/20"
                  : "border-border"
              )}
            >
              {/* Preview Image or Placeholder */}
              <div className={cn("relative aspect-video", colors.bg)}>
                {!hasImageError ? (
                  <img
                    src={template.previewUrl}
                    alt={template.name}
                    className="w-full h-full object-cover"
                    onError={() => handleImageError(template.id)}
                  />
                ) : (
                  // Placeholder with theme-based gradient
                  <div
                    className={cn(
                      "absolute inset-0 flex items-center justify-center",
                      colors.bg
                    )}
                  >
                    <div
                      className={cn(
                        "w-16 h-16 rounded-xl bg-gradient-to-br opacity-60",
                        colors.accent
                      )}
                    />
                  </div>
                )}

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />

                {/* Selected checkmark */}
                {isSelected && (
                  <div className="absolute top-2 right-2">
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shadow-lg">
                      <svg
                        className="w-4 h-4 text-primary-foreground"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4 bg-card">
                <h4
                  className={cn(
                    "font-semibold transition-colors",
                    isSelected ? "text-primary" : "text-foreground"
                  )}
                >
                  {template.name}
                </h4>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {template.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
